import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";

import { TAG_ASSESSMENT } from "../../../../Api/constants";
import FilterSliderModal from "../../../../CommonElements/FilterSliderModal";
import SearchBar from "../../../../CommonElements/SearchBar";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  getAssessmentsToBeReviewed,
  setAssessmentCatlogPayloadToInitials,
  setAssessmentReviewToInitial,
} from "../../../../ReduxToolkit/Reducers/AssessmentCatlogSlice";
import { getTagsList } from "../../../../ReduxToolkit/Reducers/MasterSlice";
import {
  AssessmentType,
  CreatedOn,
  Mins,
  Modules,
  Name,
  Questions,
  Select,
  Tags,
  Time,
  Type,
} from "../../../../utils/Constant";
import {
  assessmentTypesNames,
  assessmentTypes,
  formatCustomDate,
  createTooltipCell,
} from "../../../../utils/helper/helper";
import "./style.scss";

const customStyles = {
  rows: {
    style: {
      cursor: "pointer",
    },
  },
};

const ScheduledAssessments = () => {
  const dispatch = useAppDispatch();
  const { assessmentsToBeReviewed } = useAppSelector(
    state => state.assessmentCatlog
  );
  const { tagList } = useAppSelector(state => state.master);
  const [searchResults, setSearchResults] = useState("");
  const [selectedFilterValues, setSelectedFilterValues] = useState({});
  const [defaultFilterValues, setDefaultFilterValues] = useState({});
  const [tagsListData, setTagsListData] = useState([]);
  const [tagsListDataIdsMap, setTagsListDataIdsMap] = useState({});
  const [clearData, setClearData] = useState(false);
  const navigate = useNavigate();

  const assessmentOptions = assessmentTypes.map(item => ({
    ...item,
    name: item.value,
  }));

  const handleSearch = (query: string) => {
    setClearData(false);
    setSearchResults(query);
  };

  useEffect(() => {
    const params: { assessment_type: string[]; tag: string } = {
      assessment_type: selectedFilterValues["assessment_type"] || [],
      tag: selectedFilterValues["tags"] || "",
    };
    getAssessments(params);
    dispatch(getTagsList({ tagType: TAG_ASSESSMENT }));
  }, [dispatch]);

  useEffect(() => {
    const tags = [];
    const tagsMap = {};
    if (tagList) {
      tagList?.tags?.forEach((item, ind) => {
        tagsMap[ind] = item?.ids;
        tags.push({
          name: ind,
          value: item?.tag_name,
          label: item?.tag_name,
        });
      });
    }
    setTagsListData(tags);
    setTagsListDataIdsMap(tagsMap);
  }, [tagList]);

  const getAssessments = async params => {
    dispatch(setAssessmentReviewToInitial());
    dispatch(getAssessmentsToBeReviewed(params));
  };

  const renderRequestTable = () => {
    const tableColumns = [
      {
        name: Name,
        selector: row => row.name,
        cell: (row: any) =>
          createTooltipCell(row.name, `schedule-name-${row.id}`),
        sortable: true,
        minWidth: "15rem",
      },
      {
        name: Type,
        selector: row => assessmentTypesNames[row.assessment_type],
        sortable: true,
      },
      {
        name: Questions,
        selector: row => row.overall_question_count || "-",
        sortable: true,
      },
      {
        name: Modules,
        selector: row => row.module_count || "-",
        sortable: true,
      },
      {
        name: Time,
        selector: row =>
          row.estimated_time ? `${row.estimated_time} ${Mins}` : "-",
        sortable: true,
      },
      {
        name: CreatedOn,
        selector: row => formatCustomDate(row.created_at),
        sortable: true,
        sortFunction: (rowOne, rowTwo) => {
          const parseDate = (dateStr: string | null | undefined) => {
            if (!dateStr) return 0;
            return new Date(dateStr).getTime();
          };
          const firstParsedDate = parseDate(rowOne.created_at);
          const secondParsedDate = parseDate(rowTwo.created_at);
          return firstParsedDate - secondParsedDate;
        },
      },
    ];

    const filteredAssessment = assessmentsToBeReviewed?.assessments?.filter(
      assessment =>
        assessment.name.toLowerCase().includes(searchResults?.toLowerCase())
    );

    const handleRowClick = assessment => {
      dispatch(setAssessmentCatlogPayloadToInitials());
      const path = `/assessments/assessment-details?assessmentId=${assessment?.assessment_id}&type=${assessment?.assessment_type}&review=${true}`;
      navigate(path);
    };

    return (
      <Col>
        <Card className="p-1">
          <CardBody>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-2">
              <div></div>
              <div className="d-flex justify-content-end align-items-center mt-sm-0 mt-2 gap-2 ms-auto">
                <SearchBar
                  showSearch
                  onSearch={handleSearch}
                  clearOnProp={clearData}
                />
                <div>
                  <FilterSliderModal
                    dropdowns={[
                      {
                        label: Tags,
                        key: "tags",
                        tooltipText: `${Select} ${Tags}`,
                        options: tagsListData,
                        isMultiSelect: true,
                      },
                      {
                        label: AssessmentType,
                        key: "assessment_type",
                        tooltipText: `${Select} ${AssessmentType}`,
                        options: assessmentOptions,
                        isMultiSelect: true,
                      },
                    ]}
                    selectedFilterValues={selectedFilterValues}
                    defaultFilterValues={defaultFilterValues}
                    setSelectedFilterValues={setSelectedFilterValues}
                    setDefaultFilterValues={setDefaultFilterValues}
                    onDone={handleDone}
                    onClear={handleClear}
                  />
                </div>
              </div>
            </div>
            <DataTable
              onRowClicked={row => handleRowClick(row)}
              columns={tableColumns}
              data={filteredAssessment || []}
              pagination
              fixedHeader
              striped
              customStyles={customStyles}
            />
          </CardBody>
        </Card>
      </Col>
    );
  };

  const handleDone = async (values: { [key: string]: string[] }) => {
    setClearData(true);
    const tagIds = [];
    if (values["tags"]) {
      values["tags"]?.forEach(item => {
        if (tagsListDataIdsMap[item]) tagIds.push(...tagsListDataIdsMap[item]);
      });
    }
    const params: { assessment_type: string[]; tag: string } = {
      assessment_type: values["assessment_type"],
      tag: tagIds?.toString() || null,
    };
    getAssessments(params);
  };

  const handleClear = () => {
    setClearData(true);
    const params: { assessment_type: string[]; tag: string } = {
      assessment_type: [],
      tag: "",
    };
    getAssessments(params);
    setSelectedFilterValues({});
    setDefaultFilterValues({});
  };

  const renderAssessmentContent = () => {
    return renderRequestTable();
  };

  return (
    <div>
      <Row className="mt-2">{renderAssessmentContent()}</Row>
    </div>
  );
};

export default ScheduledAssessments;
